import _ from 'lodash';
import api from '@/api';
import {testVoter} from '../helpers';

const state = {
  meeting: {
    name: ''
  },
  loading: true
};

const getters = {
  meeting: state => state.meeting,
  loading: state => state.loading
};

const actions = {
  /**
   * Initalize the app by getting the meeting data and checking if the voter is verified.
   * @param  {Object} params                - the initalization parameters
   * @param  {String} params.shortCode      - the meeting short code
   * @param  {String} params.trackingCode   - the voter tracking code
   * @param  {String} params.token          - the signed voter token
   */
  async initializeMeeting(
    {dispatch, rootState},
    {shortCode, trackingCode, token}
  ) {
    try {
      // Set app state to loading on initialization
      dispatch('setLoading', true);
      const isPreviewMode = rootState.previewMode;

      let voterToken = null;
      if (isPreviewMode) {
        voterToken = btoa(testVoter.email);
      } else {
        voterToken = token;
      }

      const res = await api.getMeeting(shortCode, trackingCode, voterToken);

      if (!(res.status >= 200 || res.status < 300)) {
        return dispatch(
          'setError',
          {status: res.status, message: res.statusText},
          {root: true}
        );
      }

      const {data} = res;

      // If in preview mode, add test voter data
      if (rootState.previewMode) {
        data.voter = testVoter;
        if (!_.isEmpty(data.questions)) {
          data.questions = _.map(data.questions, q => {
            return {...q, isVoterEligible: true};
          });
        }
      }

      const {questions, voter} = data;

      dispatch('setMeeting', data);
      dispatch('ownership/setOwnershipAddress', data.addresses[0], {
        root: true
      });

      if (questions && Array.isArray(questions)) {
        dispatch('questions/initializeQuestions', questions, {root: true});

        // Check for elections and update nominee fields. Ignores bylaw q's.
        questions.forEach((q, index) => {
          if (
            _.includes(['owner-occupied', 'substitute-elections-oo'], q.type)
          ) {
            // Ask "is resident" question by default, if OO question(s) exist
            dispatch('resident/setShouldAskIsResident', true, {root: true});
            dispatch('setIsResidentRequirement', true, {root: true});
            // OO questions may be limited to the 'owner_occupied: true' subgroup;
            // if so, and the voter does not belong to 'owner_occupied: true' subgroup,
            // we can skip asking if they are a resident or not
            if (q.restrictions) {
              for (const restriction of q.restrictions) {
                for (const [key, value] of Object.entries(restriction)) {
                  if (
                    key === 'owner_occupied' &&
                    value === 'true' &&
                    voter.customUnitData['owner_occupied'] === false
                  ) {
                    // voter is 'owner_occupied: false'; we skip asking if they're a resident
                    dispatch('resident/setShouldAskIsResident', false, {
                      root: true
                    });
                    dispatch('setIsResidentRequirement', false, {root: true});
                  }
                }
              }
            }
            dispatch(
              'questions/updateNominees',
              {questionId: index, nominees: q.options},
              {root: true}
            );
          }

          if (
            _.includes(
              ['general-elections', 'substitute-elections', 'simple-vote'],
              q.type
            )
          ) {
            dispatch(
              'questions/updateNominees',
              {questionId: index, nominees: q.options},
              {root: true}
            );
          }
        });
        // Adds each question the the step map
        dispatch('setStepsMapWithQuestions', questions, {root: true});
      } else {
        dispatch('setStepsMapWithNoQuestions', questions, {root: true});
      }

      // Check if it's a verified voter
      if (voter) {
        const {unit, address, name, email} = voter;
        dispatch(
          'ownership/setOwnership',
          {unit, address, isFilled: true},
          {root: true}
        );
        dispatch(
          'contact/setContact',
          {name, email, isFilled: true},
          {root: true}
        );
        dispatch('voter/setVoter', {...voter, isVerified: true}, {root: true});

        // Skip comms question if owner has already consented
        // and has provided an email
        if (
          voter.consent.status === 'consented' ||
          voter.email === 'noemail@getquorum.com'
        ) {
          dispatch('setStepsSkipComms', null, {root: true});
          dispatch('comms/setComms', 'no', {root: true});
        }
      }

      // Check if there are additional app display options and add to store
      if (_.has(res.data, 'options')) {
        const {options} = res.data;

        dispatch('setMeetingOptions', options, {root: true});

        const appDisplayOptions =
          options.proxyAppDisplayOptions || options.proxy_app_display_options;
        dispatch('setAppDisplayOptions', appDisplayOptions, {root: true});

        // Ask proxyholder question before business questions
        if (_.get(appDisplayOptions, 'proxy.should_ask_first', false)) {
          dispatch('setStepsAskFirstProxyHolder', null, {root: true});
        }

        // If unverified voting is blocked
        if (options.block_unverified_voters && !isPreviewMode) {
          dispatch('setblockUnverifiedVoting', true, {root: true});
        }

        // Check if the proxy holder question needs skipped
        if (options.disable_proxy_holder) {
          dispatch('setStepsSkipProxyHolder', null, {root: true});
          // API still expects a proxy holder
          dispatch('proxy/setProxyHolder', {name: 'N/A'}, {root: true});
        }

        // Check if the comms question needs to be skipped
        if (options.disable_comms) {
          dispatch('setStepsSkipComms', null, {root: true});
          dispatch('comms/setComms', 'no', {root: true});
        }
      }
    } catch (err) {
      console.error(err);
      if (err.response) {
        return dispatch(
          'setError',
          {status: err.response.status, message: err.response.statusText},
          {root: true}
        );
      } else if (err.request) {
        // If response is null and request is defined, service is under maintenance
        return dispatch(
          'setError',
          {status: 503, message: 'Service unavailable'},
          {root: true}
        );
      }

      return dispatch(
        'setError',
        {status: err.status, message: err.statusText},
        {root: true}
      );
    } finally {
      // After meeting is initialized, set loading to false
      dispatch('setLoading', false);
    }
  },

  setMeeting({commit}, meeting) {
    return commit('SET_MEETING', meeting);
  },
  setLoading({commit}, loading) {
    return commit('SET_LOADING', loading);
  }
};

const mutations = {
  SET_MEETING(state, meeting) {
    state.meeting = meeting;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
